import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import Page from '../molecules/Page';
import { MdCheckCircle, MdError, MdSync } from 'react-icons/md';
import api from '../../api/apiHelper';
import { persistDashboardData } from '../../utils/apiCache'; 


const IS_MOBILE_BUILD = process.env.REACT_APP_IS_MOBILE === 'true';


const PARAM_KEY = IS_MOBILE_BUILD ? 'ref' : 'orderId';


const fetchRemoteStatus = IS_MOBILE_BUILD 
    ? null 
    : async (id) => {

        return api.get(`order-status/${id}/`);
    };

const StatusCard = ({ icon, title, message, buttonText, onButtonClick }) => (
    <div className="bg-white rounded-xl lg:rounded-[3rem] shadow-lg border border-gray-200 w-full max-w-lg text-center p-8 lg:p-12 flex flex-col items-center animate-fade-in">
        <div className="mb-6">{icon}</div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#233855] mb-3">{title}</h1>
        <p className="text-gray-600 mb-8">{message}</p>
        <button
            onClick={onButtonClick}
            className="w-full max-w-xs rounded-lg px-6 py-3 font-apotek text-sm font-bold uppercase tracking-widest text-white shadow-md transition-colors bg-[#233855] hover:bg-[#345081]"
        >
            {buttonText}
        </button>
    </div>
);

const PaymentStatus = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('processing'); 
    const [expiryDate, setExpiryDate] = useState(null);

    const refId = searchParams.get(PARAM_KEY); 

    const finalizeUserDataUpdate = async () => {
        try {
            console.log("Status: Syncing permissions...");
            const { data } = await api.get("user_dashboard_data/");
            persistDashboardData(data);

            if (data.account_status && data.account_status.has_access) {
                const pUserData = {
                    username: data.username,
                    problems_solved_total: data.problems_solved_total,
                    total_study_time_minutes: data.total_study_time_minutes,
                    current_streak: data.account_status.current_streak,
                };
                localStorage.setItem("paywallUserData", JSON.stringify(pUserData));

                if (data.account_status.access_expires_at) {
                    const date = new Date(data.account_status.access_expires_at * 1000);
                    if (!isNaN(date.getTime())) {
                        setExpiryDate(format(date, 'dd.MM.yyyy'));
                    }
                }
                
                setStatus('completed');
                return true;
            } else {
                return false;
            }
        } catch (e) {
            console.error("Sync error:", e);
            return false;
        }
    };

    useEffect(() => {
        let intervalId = null;
        let timeoutId = null;


        if (IS_MOBILE_BUILD || !refId) {
            const verifyLocalAccess = async () => {
                await new Promise(r => setTimeout(r, 1500));
                
                const success = await finalizeUserDataUpdate();
                
                if (!success) {
                    setTimeout(async () => {
                        const retry = await finalizeUserDataUpdate();
                        if (!retry) {
                             setStatus('failed'); 
                        }
                    }, 3000);
                }
            };
            verifyLocalAccess();
            return; 
        }

        const synchronizeRemoteStatus = async () => {
    
            if (!fetchRemoteStatus) return;

            try {
                // Wywołanie funkcji zdefiniowanej na górze pliku (tej, która znika w mobile)
                const response = await fetchRemoteStatus(refId);
                const remoteStatus = response.data.status;

                if (remoteStatus === 'COMPLETED') {
                    if (response.data.access_expires_at) {
                        const date = new Date(response.data.access_expires_at * 1000);
                        setExpiryDate(format(date, 'dd.MM.yyyy'));
                    }
                    
                    await finalizeUserDataUpdate(); 
                    
                    clearInterval(intervalId);
                    clearTimeout(timeoutId);

                } else if (remoteStatus === 'FAILED' || remoteStatus === 'CANCELED') {
                    setStatus('failed');
                    clearInterval(intervalId);
                    clearTimeout(timeoutId);
                }
            } catch (error) {
                console.error("Remote check failed:", error);
            }
        };


        if (fetchRemoteStatus) {
            synchronizeRemoteStatus(); 
            intervalId = setInterval(synchronizeRemoteStatus, 3000);

            timeoutId = setTimeout(() => {
                clearInterval(intervalId);
                if (status === 'processing') {
                    setStatus('timed_out');
                }
            }, 120000); 
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [refId, status]);

    return (
        <Page showNav={true}>
            <div className="flex justify-center items-center min-h-[80vh] px-4">
                {status === 'processing' && (
                    <StatusCard
                        icon={<MdSync className="h-20 w-20 text-[#E09F2E] animate-spin" />}
                        title="Finalizujemy..."
                        message="Proszę czekać, aktualizujemy Twoje uprawnienia w aplikacji."
                        buttonText="Wróć do zadań"
                        onButtonClick={() => navigate('/')}
                    />
                )}
                {status === 'completed' && (
                    <StatusCard
                        icon={<MdCheckCircle className="h-20 w-20 text-green-500" />}
                        title="Gotowe!"
                        message={expiryDate 
                            ? `Płatność zakończona sukcesem. Dostęp ważny do ${expiryDate}. Możesz wracać do nauki!`
                            : "Płatność zakończona sukcesem. Możesz wracać do nauki!"
                        }
                        buttonText="Wróć do zadań"
                        onButtonClick={() => navigate('/')}
                    />
                )}
                {(status === 'failed' || status === 'timed_out') && (
                    <StatusCard
                        icon={<MdError className="h-20 w-20 text-red-500" />}
                        title="Problem z weryfikacją"
                        message={status === 'timed_out'
                            ? "Weryfikacja trwa zbyt długo. Sprawdź status później."
                            : "Nie udało się zweryfikować uprawnień. Spróbuj opcji 'Przywróć' w ustawieniach."
                        }
                        buttonText="Wróć"
                        onButtonClick={() => navigate('/upgrade')}
                    />
                )}
            </div>
        </Page>
    );
};

export default PaymentStatus;